import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { saveShippingInfo } from "../../slices/cartSlice";
import { useNavigate } from "react-router-dom";
import CheckoutSteps from "./CheckoutSteps";

export const Shiping = () => {
    const { shippingInfo } = useSelector((state) => state.cartState);
    const { user } = useSelector((state) => state.authState);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Use internal state for form fields
    const [fullname, setFullname] = useState("");
    const [phone_No, setPhone_No] = useState("");
    const [email, setEmail] = useState("");
    const [house_No, setHouse_No] = useState("");
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    const [district, setDistrict] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [errors, setErrors] = useState({});

    // Reactive State Synchronization
    useEffect(() => {
        // 1. Prioritize saved shipping info from previous session/local storage
        if (shippingInfo?.fullname) setFullname(shippingInfo.fullname);
        else if (user?.name) setFullname(user.name); // Fallback to profile name

        if (shippingInfo?.email) setEmail(shippingInfo.email);
        else if (user?.email) setEmail(user.email); // Fallback to profile email

        if (shippingInfo?.phone_No) setPhone_No(shippingInfo.phone_No);
        if (shippingInfo?.house_No) setHouse_No(shippingInfo.house_No);
        if (shippingInfo?.street) setStreet(shippingInfo.street);
        if (shippingInfo?.city) setCity(shippingInfo.city);
        if (shippingInfo?.district) setDistrict(shippingInfo.district);
        if (shippingInfo?.postalCode) setPostalCode(shippingInfo.postalCode);
    }, [shippingInfo, user]);

    const handleSubmit = (e) => {
        e.preventDefault();
        let formErrors = {};

        if (!fullname) formErrors.fullname = "Full Name is required";
        if (!phone_No) formErrors.phoneno = "Phone Number is required";
        
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) formErrors.email = "Email is required";
        else if (!emailPattern.test(email)) formErrors.email = "Invalid email format";

        const phonePattern = /^\d{10}$/;
        if (phone_No && !phonePattern.test(phone_No)) formErrors.phoneno = "Invalid phone number (10 digits)";

        if (!house_No) formErrors.houseNo = "House No is required";
        if (!street) formErrors.street = "Street is required";
        if (!city) formErrors.city = "City is required";
        if (!district) formErrors.district = "District is required";
        if (!postalCode) formErrors.postalcode = "Postal Code is required";

        if (Object.keys(formErrors).length === 0) {
            dispatch(saveShippingInfo({ fullname, phone_No, email, house_No, street, city, district, postalCode }));
            navigate('/orderconfrim');
        } else {
            setErrors(formErrors);
        }
    };

    const inputClasses = (error) => `
        w-full bg-white text-gray-900 px-6 py-4 rounded-2xl border 
        transition-all duration-300 outline-none text-sm font-medium
        ${error ? 'border-red-400 focus:border-red-500 bg-red-50/10' : 'border-gray-100 focus:border-yellow-600 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)]'}
    `;

    return (
        <div className="bg-white min-h-screen pt-24 pb-32">
            <div className="container mx-auto px-4">
                {/* Header & Steps */}
                <div className="max-w-4xl mx-auto mb-16">
                    <h1 className="text-4xl lg:text-5xl font-serif font-bold text-center text-gray-900 mb-8">Shipping Gallery</h1>
                    <CheckoutSteps shipping />
                </div>

                <div className="max-w-2xl mx-auto">
                    <div className="bg-gray-50/50 rounded-[2.5rem] p-8 lg:p-12 border border-gray-100 shadow-sm">
                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="text-center mb-10">
                                <h2 className="text-xs font-bold tracking-[.3em] text-yellow-600 uppercase mb-2">Delivery Details</h2>
                                <p className="text-gray-400 text-sm font-light">Please provide your insured shipping information</p>
                            </div>

                            {/* Full Name */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Alexander Pierce"
                                    value={fullname}
                                    onChange={(e) => setFullname(e.target.value)}
                                    className={inputClasses(errors.fullname)}
                                />
                                {errors.fullname && <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-4">{errors.fullname}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Email */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        placeholder="alex@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={inputClasses(errors.email)}
                                    />
                                    {errors.email && <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-4">{errors.email}</p>}
                                </div>

                                {/* Phone */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                                    <input
                                        type="text"
                                        placeholder="10 Digit Number"
                                        value={phone_No}
                                        onChange={(e) => setPhone_No(e.target.value)}
                                        className={inputClasses(errors.phoneno)}
                                    />
                                    {errors.phoneno && <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-4">{errors.phoneno}</p>}
                                </div>
                            </div>

                            {/* Address Row 1 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">House / Apt No</label>
                                    <input
                                        type="text"
                                        placeholder="Building Info"
                                        value={house_No}
                                        onChange={(e) => setHouse_No(e.target.value)}
                                        className={inputClasses(errors.houseNo)}
                                    />
                                    {errors.houseNo && <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-4">{errors.houseNo}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Street Name</label>
                                    <input
                                        type="text"
                                        placeholder="Locality"
                                        value={street}
                                        onChange={(e) => setStreet(e.target.value)}
                                        className={inputClasses(errors.street)}
                                    />
                                    {errors.street && <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-4">{errors.street}</p>}
                                </div>
                            </div>

                            {/* Address Row 2 */}
                            <div className="grid grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">City</label>
                                    <input
                                        type="text"
                                        placeholder="City"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        className={inputClasses(errors.city)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">District</label>
                                    <input
                                        type="text"
                                        placeholder="Region"
                                        value={district}
                                        onChange={(e) => setDistrict(e.target.value)}
                                        className={inputClasses(errors.district)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Pincode</label>
                                    <input
                                        type="text"
                                        placeholder="ZIP"
                                        value={postalCode}
                                        onChange={(e) => setPostalCode(e.target.value)}
                                        className={inputClasses(errors.postalcode)}
                                    />
                                </div>
                            </div>

                            <div className="pt-8 text-center">
                                <button
                                    type="submit"
                                    className="w-full md:w-auto bg-gray-900 text-white px-16 py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-yellow-600 transition-all shadow-[0_20px_50px_rgba(0,0,0,0.1)] hover:shadow-yellow-600/20 active:scale-95"
                                >
                                    Review Order Details
                                </button>
                                <p className="text-[9px] text-gray-400 uppercase tracking-widest mt-6 font-medium">
                                    Trusted Insured Shipping Powered by IGold
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
